import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { FaFileExport } from "react-icons/fa";
import ApplicationTable from "../components/application/ApplicationTable";
import { getApplication, getExport } from "../api/services/ApplicationService";

const Application = () => {
    const navigate = useNavigate();

    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    // Pagination & Search State
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [searchTerm, setSearchTerm] = useState(""); // Immediate UI state
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [limit] = useState(10);

    /* -------------------------------------------------------------------------- */
    /* FETCH APPLICATIONS                                                          */
    /* -------------------------------------------------------------------------- */
    const fetchApplications = useCallback(async (currentPage, currentSearch) => {
        try {
            setLoading(true);
            setError(null);

            const response = await getApplication({
                page: currentPage,
                limit: limit,
                search: currentSearch
            });

            setApplications(response?.data || []);
            setTotalItems(response?.pagination?.totalItems || 0);
            setTotalPages(response?.pagination?.totalPages || 0);
        } catch (err) {
            console.error("Error fetching applications:", err);
            setError("Failed to load applications");
            setApplications([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    /* -------------------------------------------------------------------------- */
    /* DEBOUNCED SEARCH                                                            */
    /* -------------------------------------------------------------------------- */
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(searchTerm);
            setPage(1); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchApplications(page, search);
    }, [page, search, fetchApplications]);

    /* -------------------------------------------------------------------------- */
    /* EXPORT APPLICATIONS                                                         */
    /* -------------------------------------------------------------------------- */
    const handleExport = async () => {
        if (exportLoading || applications.length === 0) return;

        try {
            setExportLoading(true);
            setError(null);

            await getExport();
        } catch (err) {
            console.error("Export failed:", err);
            setError("Failed to export applications");
        } finally {
            setExportLoading(false);
        }
    };

    /* -------------------------------------------------------------------------- */
    /* STATUS UPDATE HANDLER                                                       */
    /* -------------------------------------------------------------------------- */
    const handleStatusUpdate = (applicationId, newStatus) => {
        setApplications(prev =>
            prev.map(app =>
                app._id === applicationId
                    ? { ...app, status: newStatus }
                    : app
            )
        );
    };

    /* -------------------------------------------------------------------------- */
    /* UI STATES                                                                   */
    /* -------------------------------------------------------------------------- */
    if (loading && applications.length === 0) {
        return (
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "50vh" }}
            >
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3">Loading applications...</p>
            </div>
        );
    }

    /* -------------------------------------------------------------------------- */
    /* MAIN VIEW                                                                   */
    /* -------------------------------------------------------------------------- */
    return (
        <div>
            <header className="content-header d-flex justify-content-between align-items-center mb-2">
                <h1 className="mb-0">Applications</h1>

                <Button
                    variant="outline-primary"
                    onClick={handleExport}
                    disabled={exportLoading || (applications.length === 0 && !search)}
                    className="d-flex align-items-center"
                >
                    {exportLoading ? (
                        <>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                            Exporting...
                        </>
                    ) : (
                        <>
                            <FaFileExport className="me-2" />
                            Export
                        </>
                    )}
                </Button>
            </header>

            {error && (
                <div className="alert alert-danger mx-3 mt-3 d-flex justify-content-between align-items-center">
                    <span>{error}</span>
                    <Button variant="outline-danger" size="sm" onClick={() => fetchApplications(page, search)}>
                        Retry
                    </Button>
                </div>
            )}

            <ApplicationTable
                data={applications}
                onStatusUpdate={handleStatusUpdate}
                refreshData={() => fetchApplications(page, search)}
                totalItems={totalItems}
                totalPages={totalPages}
                page={page}
                onPageChange={setPage}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                loading={loading}
            />
        </div>
    );
};

export default Application;
